import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('hi-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Helper to check if date is overdue
const isOverdue = (dateStr) => {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
};

// Progress Bar Component
const ProgressBar = ({ value, max, color = 'orange' }) => {
  const percent = max > 0 ? Math.round((value / max) * 100) : 0;
  const colors = {
    orange: 'bg-orange-500',
    green: 'bg-green-500',
    blue: 'bg-blue-500',
    red: 'bg-red-500'
  };
  return (
    <div className="w-full bg-gray-200 rounded-full h-3">
      <div 
        className={`h-3 rounded-full ${colors[color]} transition-all duration-500`}
        style={{ width: `${percent}%` }}
      />
    </div>
  );
};

// Stat Card for Dashboard
const StatCard = ({ title, value, icon, color = 'orange', subtitle }) => {
  const colors = {
    orange: 'bg-gradient-to-br from-orange-500 to-amber-500 text-white',
    green: 'bg-gradient-to-br from-green-500 to-emerald-500 text-white',
    blue: 'bg-gradient-to-br from-blue-500 to-indigo-500 text-white',
    red: 'bg-gradient-to-br from-red-500 to-rose-500 text-white',
    purple: 'bg-gradient-to-br from-purple-500 to-violet-500 text-white',
    gray: 'bg-gradient-to-br from-gray-500 to-slate-600 text-white'
  };
  return (
    <div className={`${colors[color]} rounded-xl p-5 shadow-lg`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm opacity-80">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-xs opacity-70 mt-1">{subtitle}</p>}
        </div>
        <span className="text-4xl opacity-60">{icon}</span>
      </div>
    </div>
  );
};

// Task Form Modal
const TaskFormModal = ({ task, student, onClose, onSubmit, language }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      // If mother_working_status changes to "Non Working", clear mother_occupation
      if (field === 'mother_working_status' && value === 'Non Working') {
        newData['mother_occupation'] = 'Non Working';
      }
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onSubmit(task.id, formData);
    setLoading(false);
  };

  const missingFields = task?.missing_fields || [];
  
  // Mother occupation options
  const motherOccupationOptions = [
    "Teacher", "Doctor", "Nurse", "Government Job", "Private Job", 
    "Business", "Bank Employee", "Police", "Anganwadi Worker", 
    "ASHA Worker", "Other"
  ];

  // Check if mother_working_status is "Working"
  const isMotherWorking = formData['mother_working_status'] === 'Working';

  // Filter fields - skip mother_occupation if mother is not working
  const visibleFields = missingFields.filter(field => {
    if (field.field === 'mother_occupation' && !isMotherWorking) {
      return false;
    }
    return true;
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-5">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-xl font-bold">{student?.name || task?.student_name}</h3>
              {(student?.name_hindi || task?.student_name_hindi) && (
                <p className="text-orange-100 text-sm">{student?.name_hindi || task?.student_name_hindi}</p>
              )}
              <p className="text-orange-100 text-sm mt-1">
                {task?.class_name} {task?.section_name && `| Section ${task?.section_name}`}
              </p>
            </div>
            <button 
              onClick={onClose}
              className="text-white/80 hover:text-white text-2xl"
            >
              &times;
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[60vh]">
          <p className="text-gray-600 mb-4">
            {language === 'hi' 
              ? `निम्नलिखित ${visibleFields.length} जानकारियाँ भरें:`
              : `Fill in the following ${visibleFields.length} missing details:`}
          </p>

          <div className="space-y-4">
            {visibleFields.map((field, idx) => (
              <div key={field.field} className="border rounded-lg p-4 bg-gray-50">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                    field.priority === 'high' ? 'bg-red-500' : 
                    field.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                  }`}></span>
                  {language === 'hi' ? field.label_hi : field.label}
                  {field.priority === 'high' && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                
                {field.field === 'gender' ? (
                  <select
                    value={formData[field.field] || ''}
                    onChange={(e) => handleChange(field.field, e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">{language === 'hi' ? 'चुनें' : 'Select'}</option>
                    <option value="male">{language === 'hi' ? 'पुरुष' : 'Male'}</option>
                    <option value="female">{language === 'hi' ? 'महिला' : 'Female'}</option>
                    <option value="other">{language === 'hi' ? 'अन्य' : 'Other'}</option>
                  </select>
                ) : field.field === 'category' ? (
                  <select
                    value={formData[field.field] || ''}
                    onChange={(e) => handleChange(field.field, e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">{language === 'hi' ? 'चुनें' : 'Select'}</option>
                    <option value="general">General</option>
                    <option value="obc">OBC</option>
                    <option value="sc">SC</option>
                    <option value="st">ST</option>
                    <option value="ews">EWS</option>
                  </select>
                ) : field.field === 'mother_working_status' ? (
                  <select
                    value={formData[field.field] || ''}
                    onChange={(e) => handleChange(field.field, e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">{language === 'hi' ? 'चुनें' : 'Select'}</option>
                    <option value="Non Working">{language === 'hi' ? 'गृहिणी (Non Working)' : 'Non Working'}</option>
                    <option value="Working">{language === 'hi' ? 'कार्यरत (Working)' : 'Working'}</option>
                  </select>
                ) : field.field === 'mother_occupation' ? (
                  <select
                    value={formData[field.field] || ''}
                    onChange={(e) => handleChange(field.field, e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">{language === 'hi' ? 'चुनें' : 'Select'}</option>
                    {motherOccupationOptions.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : field.field === 'dob' ? (
                  <input
                    type="date"
                    value={formData[field.field] || ''}
                    onChange={(e) => handleChange(field.field, e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                ) : field.field.includes('phone') || field.field === 'aadhaar_number' ? (
                  <input
                    type="tel"
                    value={formData[field.field] || ''}
                    onChange={(e) => handleChange(field.field, e.target.value)}
                    placeholder={field.field === 'aadhaar_number' ? '1234 5678 9012' : '98765 43210'}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                ) : (
                  <input
                    type="text"
                    value={formData[field.field] || ''}
                    onChange={(e) => handleChange(field.field, e.target.value)}
                    placeholder={language === 'hi' ? field.label_hi : field.label}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              {language === 'hi' ? 'रद्द करें' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={loading || Object.keys(formData).length === 0}
              className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg hover:from-orange-600 hover:to-amber-600 disabled:opacity-50"
            >
              {loading 
                ? (language === 'hi' ? 'सेव हो रहा है...' : 'Saving...') 
                : (language === 'hi' ? 'सेव करें' : 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Data Tasks Page
export default function DataTasksPage({ token, user }) {
  const { language } = useLanguage();
  const [activeView, setActiveView] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [taskStats, setTaskStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [filters, setFilters] = useState({
    assigned_to: 'all',
    class_name: '',
    status: '',
    priority: '',
    school_id: 'all'  // For platform admin
  });
  const [toast, setToast] = useState(null);
  const [schoolsList, setSchoolsList] = useState([]);  // For platform admin dropdown

  // Determine user role for filtering
  const userRole = user?.role;
  const isPlatformAdmin = userRole === 'platform_admin';
  const isAdmin = ['platform_admin', 'school_admin', 'admin'].includes(userRole);
  const isTeacher = userRole === 'teacher';
  const isParent = userRole === 'parent';

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/data-completion/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setDashboard(data.dashboard);
      }
    } catch (err) {
      console.error('Error loading dashboard:', err);
    }
  }, [token]);

  // Load tasks with filters
  const loadTasks = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.assigned_to && filters.assigned_to !== 'all') {
        params.append('assigned_to', filters.assigned_to);
      }
      if (filters.class_name) params.append('class_name', filters.class_name);
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      // Platform admin school filter
      if (isPlatformAdmin && filters.school_id && filters.school_id !== 'all') {
        params.append('school_id_filter', filters.school_id);
      }

      const res = await fetch(`${API_URL}/api/data-completion/tasks?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setTasks(data.tasks || []);
        setTaskStats(data.stats);
      }
    } catch (err) {
      console.error('Error loading tasks:', err);
    }
  }, [token, filters, isPlatformAdmin]);

  // Load student details
  const loadStudentTasks = async (studentId) => {
    try {
      const res = await fetch(`${API_URL}/api/data-completion/tasks/student/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setSelectedStudent(data.student);
        // Don't override selectedTask here - it's already set by the click handler
      }
    } catch (err) {
      console.error('Error loading student tasks:', err);
    }
  };

  // Complete task
  const handleCompleteTask = async (taskId, fieldData) => {
    try {
      const res = await fetch(`${API_URL}/api/data-completion/tasks/${taskId}/complete`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(fieldData)
      });
      const data = await res.json();
      if (data.success) {
        setToast({
          type: 'success',
          message: language === 'hi' 
            ? `डेटा सेव हो गया! ${data.remaining_fields} fields बाकी`
            : `Data saved! ${data.remaining_fields} fields remaining`
        });
        setSelectedTask(null);
        setSelectedStudent(null);
        loadTasks();
        loadDashboard();
      } else {
        setToast({ type: 'error', message: data.detail || 'Error saving data' });
      }
    } catch (err) {
      setToast({ type: 'error', message: 'Network error' });
    }
  };

  // Load schools list for platform admin
  const loadSchoolsList = useCallback(async () => {
    if (!isPlatformAdmin) return;
    try {
      const res = await fetch(`${API_URL}/api/data-completion/schools-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setSchoolsList(data.schools || []);
      }
    } catch (err) {
      console.error('Error loading schools list:', err);
    }
  }, [token, isPlatformAdmin]);

  // Helper function to get short field names based on language
  const getShortFieldName = (field, lang = language) => {
    const shortNamesHi = {
      'admission_number': 'प्रवेश नं.',
      'roll_number': 'रोल नं.',
      'name': 'नाम',
      'gender': 'लिंग',
      'dob': 'जन्मतिथि',
      'class_name': 'कक्षा',
      'religion': 'धर्म',
      'caste': 'जाति',
      'category': 'वर्ग',
      'father_name': 'पिता',
      'mother_name': 'माता',
      'father_occupation': 'पिता व्यवसाय',
      'mother_occupation': 'माता व्यवसाय',
      'mother_working_status': 'माता कार्य',
      'primary_phone': 'फोन',
      'secondary_phone': 'फोन 2',
      'address_line1': 'पता',
      'address_city': 'शहर',
      'address_district': 'जिला',
      'address_pincode': 'पिनकोड',
      'aadhaar_number': 'आधार',
      'samagra_id': 'समग्र',
      'family_id': 'फैमिली ID',
      'bank_account_number': 'बैंक खाता',
      'bank_ifsc': 'IFSC',
      'caste_certificate': 'जाति प्रमाण',
      'domicile_certificate': 'मूल निवास'
    };
    
    const shortNamesEn = {
      'admission_number': 'Adm No.',
      'roll_number': 'Roll No.',
      'name': 'Name',
      'gender': 'Gender',
      'dob': 'DOB',
      'class_name': 'Class',
      'religion': 'Religion',
      'caste': 'Caste',
      'category': 'Category',
      'father_name': 'Father',
      'mother_name': 'Mother',
      'father_occupation': 'Father Occ.',
      'mother_occupation': 'Mother Occ.',
      'mother_working_status': 'Mother Work',
      'primary_phone': 'Phone',
      'secondary_phone': 'Phone 2',
      'address_line1': 'Address',
      'address_city': 'City',
      'address_district': 'District',
      'address_pincode': 'Pincode',
      'aadhaar_number': 'Aadhaar',
      'samagra_id': 'Samagra',
      'family_id': 'Family ID',
      'bank_account_number': 'Bank A/C',
      'bank_ifsc': 'IFSC',
      'caste_certificate': 'Caste Cert.',
      'domicile_certificate': 'Domicile'
    };
    
    const shortNames = lang === 'hi' ? shortNamesHi : shortNamesEn;
    return shortNames[field] || field;
  };

  // Export to Excel
  const handleExportExcel = () => {
    const headers = language === 'hi' 
      ? ['क्र.', 'छात्र का नाम', 'कक्षा', 'Section', 'किसके लिए', 'Pending Fields', 'Deadline', 'Status']
      : ['Sr', 'Student Name', 'Class', 'Section', 'Assigned To', 'Pending Fields', 'Deadline', 'Status'];
    
    const rows = tasks.map((task, idx) => {
      const fieldNames = task.missing_fields?.map(f => getShortFieldName(f.field)).join(', ') || '-';
      return [
        idx + 1,
        task.student_name || '-',
        task.class_name || '-',
        task.section_name || 'A',
        task.assigned_to === 'teacher' 
          ? (language === 'hi' ? 'शिक्षक' : 'Teacher') 
          : (language === 'hi' ? 'अभिभावक' : 'Parent'),
        fieldNames,
        formatDate(task.deadline),
        task.status === 'completed' ? 'Done' : task.status === 'in_progress' ? 'In Progress' : 'Pending'
      ];
    });

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Add BOM for Excel to recognize UTF-8
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Data_Tasks_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    setToast({
      type: 'success',
      message: language === 'hi' ? 'Excel फाइल download हो गई!' : 'Excel file downloaded!'
    });
  };

  // Print / PDF
  const handlePrintPDF = () => {
    const printWindow = window.open('', '_blank');
    const currentDate = new Date().toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-US', { 
      day: '2-digit', month: 'long', year: 'numeric' 
    });

    const tableRows = tasks.map((task, idx) => {
      const fieldNames = task.missing_fields?.map(f => 
        `<span class="field-tag ${f.priority === 'high' ? 'high' : f.priority === 'medium' ? 'medium' : 'low'}">${getShortFieldName(f.field)}</span>`
      ).join(' ') || '-';
      
      return `
        <tr class="${task.status === 'completed' ? 'completed' : isOverdue(task.deadline) && task.status === 'pending' ? 'overdue' : ''}">
          <td>${idx + 1}</td>
          <td><strong>${task.student_name}</strong><br><small>Section - ${task.section_name || 'A'}</small></td>
          <td>${task.class_name}</td>
          <td>${task.assigned_to === 'teacher' 
            ? (language === 'hi' ? 'शिक्षक' : 'Teacher') 
            : (language === 'hi' ? 'अभिभावक' : 'Parent')}</td>
          <td class="fields-cell">${fieldNames}</td>
          <td>${formatDate(task.deadline)}</td>
          <td>${task.status === 'completed' ? '✅' : task.status === 'in_progress' ? '🔄' : '⏳'}</td>
        </tr>
      `;
    }).join('');

    const reportTitle = language === 'hi' ? '📋 डेटा पूर्णता रिपोर्ट' : '📋 Data Completion Report';
    const reportSubtitle = language === 'hi' ? 'छात्रों की अपूर्ण जानकारी' : 'Incomplete Student Information';
    const totalLabel = language === 'hi' ? 'कुल' : 'Total';
    const srLabel = language === 'hi' ? 'क्र.' : 'Sr';
    const studentLabel = language === 'hi' ? 'छात्र' : 'Student';
    const classLabel = language === 'hi' ? 'कक्षा' : 'Class';
    const assignedLabel = language === 'hi' ? 'किसके लिए' : 'Assigned To';
    const footerText = language === 'hi' ? 'शिक्षा मित्र द्वारा निर्मित' : 'Generated by Shiksha Mitra';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Data Completion Tasks Report</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; font-size: 11px; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #f97316; padding-bottom: 15px; }
          .header h1 { color: #ea580c; font-size: 20px; }
          .header p { color: #666; margin-top: 5px; }
          .stats { display: flex; justify-content: center; gap: 20px; margin-bottom: 20px; }
          .stat-box { padding: 10px 20px; border-radius: 8px; text-align: center; }
          .stat-box.total { background: #f3f4f6; }
          .stat-box.pending { background: #fef3c7; }
          .stat-box.overdue { background: #fee2e2; }
          .stat-box span { display: block; font-size: 18px; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th { background: #f97316; color: white; padding: 10px 6px; text-align: left; font-size: 10px; }
          td { padding: 8px 6px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
          tr:hover { background: #fff7ed; }
          tr.completed { background: #f0fdf4; }
          tr.overdue { background: #fef2f2; }
          .fields-cell { max-width: 200px; }
          .field-tag { display: inline-block; padding: 2px 6px; margin: 1px; border-radius: 4px; font-size: 9px; }
          .field-tag.high { background: #fee2e2; color: #dc2626; }
          .field-tag.medium { background: #fef3c7; color: #d97706; }
          .field-tag.low { background: #e5e7eb; color: #4b5563; }
          .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #666; }
          @media print {
            body { padding: 10px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${reportTitle}</h1>
          <p>${reportSubtitle} | ${currentDate}</p>
        </div>
        
        <div class="stats">
          <div class="stat-box total"><span>${taskStats?.total || tasks.length}</span>${totalLabel}</div>
          <div class="stat-box pending"><span>${taskStats?.pending || 0}</span>Pending</div>
          <div class="stat-box overdue"><span>${taskStats?.overdue || 0}</span>Overdue</div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width:30px">${srLabel}</th>
              <th style="width:120px">${studentLabel}</th>
              <th style="width:60px">${classLabel}</th>
              <th style="width:60px">${assignedLabel}</th>
              <th>Pending Fields</th>
              <th style="width:80px">Deadline</th>
              <th style="width:40px">Status</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>

        <div class="footer">
          <p>${footerText} | ${totalLabel}: ${tasks.length} tasks</p>
        </div>

        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      // Load schools list for platform admin first
      if (isPlatformAdmin) {
        await loadSchoolsList();
      }
      await Promise.all([loadDashboard(), loadTasks()]);
      setLoading(false);
    };
    init();
  }, [loadDashboard, loadTasks, loadSchoolsList, isPlatformAdmin]);

  // Reload tasks when filters change
  useEffect(() => {
    if (!loading) loadTasks();
  }, [filters]);

  // Get unique classes from dashboard
  const classes = dashboard?.by_class?.map(c => c.class_name) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {language === 'hi' ? 'लोड हो रहा है...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-tasks-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            {language === 'hi' ? '📋 डेटा पूर्णता Tasks' : '📋 Data Completion Tasks'}
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            {language === 'hi' 
              ? 'छात्रों की अधूरी जानकारी भरें और track करें'
              : 'Fill and track incomplete student information'}
          </p>
        </div>

        {/* View Toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveView('dashboard')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              activeView === 'dashboard' ? 'bg-white shadow text-orange-600' : 'text-gray-600'
            }`}
            data-testid="view-dashboard-btn"
          >
            {language === 'hi' ? '📊 Dashboard' : '📊 Dashboard'}
          </button>
          <button
            onClick={() => setActiveView('tasks')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              activeView === 'tasks' ? 'bg-white shadow text-orange-600' : 'text-gray-600'
            }`}
            data-testid="view-tasks-btn"
          >
            {language === 'hi' ? '📝 Tasks List' : '📝 Tasks List'}
          </button>
        </div>
      </div>

      {/* Dashboard View */}
      {activeView === 'dashboard' && dashboard && (
        <div className="space-y-6">
          {/* Overall Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <StatCard
              title={language === 'hi' ? 'कुल छात्र' : 'Total Students'}
              value={dashboard.total_students}
              icon="👨‍🎓"
              color="blue"
            />
            <StatCard
              title={language === 'hi' ? 'कुल Tasks' : 'Total Tasks'}
              value={dashboard.total_tasks}
              icon="📋"
              color="purple"
            />
            <StatCard
              title={language === 'hi' ? 'Pending' : 'Pending'}
              value={dashboard.pending_tasks}
              icon="⏳"
              color="orange"
            />
            <StatCard
              title={language === 'hi' ? 'पूर्ण' : 'Completed'}
              value={dashboard.completed_tasks}
              icon="✅"
              color="green"
            />
            <StatCard
              title={language === 'hi' ? 'Overdue' : 'Overdue'}
              value={dashboard.overdue_tasks}
              icon="⚠️"
              color="red"
            />
            <StatCard
              title={language === 'hi' ? 'Completion' : 'Completion'}
              value={`${dashboard.completion_percent}%`}
              icon="📈"
              color="gray"
            />
          </div>

          {/* Overall Progress */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-semibold text-gray-800 mb-4">
              {language === 'hi' ? '📊 समग्र प्रगति' : '📊 Overall Progress'}
            </h3>
            <div className="mb-2 flex justify-between text-sm">
              <span>{dashboard.completed_tasks} / {dashboard.total_tasks} Tasks</span>
              <span className="font-medium text-orange-600">{dashboard.completion_percent}%</span>
            </div>
            <ProgressBar 
              value={dashboard.completed_tasks} 
              max={dashboard.total_tasks}
              color={dashboard.completion_percent >= 80 ? 'green' : dashboard.completion_percent >= 50 ? 'orange' : 'red'}
            />
          </div>

          {/* By Role */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Teacher Tasks */}
            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-2xl">
                  👨‍🏫
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">
                    {language === 'hi' ? 'शिक्षक Tasks' : 'Teacher Tasks'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {language === 'hi' ? 'छात्र का मूल विवरण' : 'Student basic details'}
                  </p>
                </div>
              </div>
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {dashboard.teacher_pending}
                <span className="text-sm font-normal text-gray-500 ml-2">pending</span>
              </div>
              <ProgressBar 
                value={dashboard.total_tasks/2 - dashboard.teacher_pending} 
                max={dashboard.total_tasks/2}
                color="blue"
              />
            </div>

            {/* Parent Tasks */}
            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-2xl">
                  👪
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">
                    {language === 'hi' ? 'अभिभावक Tasks' : 'Parent Tasks'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {language === 'hi' ? 'पारिवारिक और दस्तावेज़ विवरण' : 'Family & document details'}
                  </p>
                </div>
              </div>
              <div className="text-3xl font-bold text-green-600 mb-2">
                {dashboard.parent_pending}
                <span className="text-sm font-normal text-gray-500 ml-2">pending</span>
              </div>
              <ProgressBar 
                value={dashboard.total_tasks/2 - dashboard.parent_pending} 
                max={dashboard.total_tasks/2}
                color="green"
              />
            </div>
          </div>

          {/* By Class */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-semibold text-gray-800 mb-4">
              {language === 'hi' ? '📚 कक्षा के अनुसार Pending Tasks' : '📚 Pending Tasks by Class'}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {dashboard.by_class.map((classData) => (
                <button
                  key={classData.class_name}
                  onClick={() => {
                    setFilters(prev => ({ ...prev, class_name: classData.class_name }));
                    setActiveView('tasks');
                  }}
                  className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 text-center hover:shadow-lg transition border-2 border-transparent hover:border-orange-300"
                  data-testid={`class-card-${classData.class_name}`}
                >
                  <p className="text-sm text-gray-600 mb-1">{classData.class_name}</p>
                  <p className="text-2xl font-bold text-orange-600">{classData.pending}</p>
                  <p className="text-xs text-gray-500">pending</p>
                </button>
              ))}
            </div>
          </div>

          {/* Quick Action for Admin */}
          {isAdmin && (
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-200">
              <h3 className="font-semibold text-orange-800 mb-2">
                {language === 'hi' ? '💡 Quick Tip' : '💡 Quick Tip'}
              </h3>
              <p className="text-orange-700 text-sm">
                {language === 'hi' 
                  ? 'किसी भी class card पर click करके उस class की tasks देखें और भरें।'
                  : 'Click on any class card to view and fill tasks for that class.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Tasks List View */}
      {activeView === 'tasks' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-xl shadow p-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {/* School Filter - Platform Admin Only */}
              {isPlatformAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'hi' ? '🏫 स्कूल' : '🏫 School'}
                  </label>
                  <select
                    value={filters.school_id}
                    onChange={(e) => setFilters(prev => ({ ...prev, school_id: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                    data-testid="filter-school"
                  >
                    <option value="all">{language === 'hi' ? 'सभी स्कूल' : 'All Schools'}</option>
                    {schoolsList.map(school => (
                      <option key={school.id} value={school.id}>
                        {school.name} ({school.pending_tasks} pending)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Role Filter */}
              {isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'hi' ? 'किसके लिए' : 'Assigned To'}
                  </label>
                  <select
                    value={filters.assigned_to}
                    onChange={(e) => setFilters(prev => ({ ...prev, assigned_to: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                    data-testid="filter-assigned-to"
                  >
                    <option value="all">{language === 'hi' ? 'सभी' : 'All'}</option>
                    <option value="teacher">{language === 'hi' ? 'शिक्षक' : 'Teacher'}</option>
                    <option value="parent">{language === 'hi' ? 'अभिभावक' : 'Parent'}</option>
                  </select>
                </div>
              )}

              {/* Class Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'hi' ? 'कक्षा' : 'Class'}
                </label>
                <select
                  value={filters.class_name}
                  onChange={(e) => setFilters(prev => ({ ...prev, class_name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  data-testid="filter-class"
                >
                  <option value="">{language === 'hi' ? 'सभी कक्षाएं' : 'All Classes'}</option>
                  {classes.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'hi' ? 'स्थिति' : 'Status'}
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  data-testid="filter-status"
                >
                  <option value="">{language === 'hi' ? 'सभी' : 'All'}</option>
                  <option value="pending">{language === 'hi' ? 'Pending' : 'Pending'}</option>
                  <option value="in_progress">{language === 'hi' ? 'In Progress' : 'In Progress'}</option>
                  <option value="completed">{language === 'hi' ? 'Completed' : 'Completed'}</option>
                  <option value="overdue">{language === 'hi' ? 'Overdue' : 'Overdue'}</option>
                </select>
              </div>

              {/* Priority Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'hi' ? 'प्राथमिकता' : 'Priority'}
                </label>
                <select
                  value={filters.priority}
                  onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  data-testid="filter-priority"
                >
                  <option value="">{language === 'hi' ? 'सभी' : 'All'}</option>
                  <option value="high">{language === 'hi' ? 'High' : 'High'}</option>
                  <option value="medium">{language === 'hi' ? 'Medium' : 'Medium'}</option>
                  <option value="low">{language === 'hi' ? 'Low' : 'Low'}</option>
                </select>
              </div>
            </div>

            {/* Clear Filters */}
            {(filters.assigned_to !== 'all' || filters.class_name || filters.status || filters.priority || filters.school_id !== 'all') && (
              <button
                onClick={() => setFilters({ assigned_to: 'all', class_name: '', status: '', priority: '', school_id: 'all' })}
                className="mt-3 text-sm text-orange-600 hover:text-orange-800"
              >
                {language === 'hi' ? '✕ Filters हटाएं' : '✕ Clear Filters'}
              </button>
            )}
          </div>

          {/* Stats Summary */}
          {taskStats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-white rounded-lg p-3 text-center shadow">
                <p className="text-2xl font-bold text-gray-800">{taskStats.total}</p>
                <p className="text-xs text-gray-500">{language === 'hi' ? 'कुल' : 'Total'}</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-3 text-center shadow">
                <p className="text-2xl font-bold text-yellow-600">{taskStats.pending}</p>
                <p className="text-xs text-gray-500">Pending</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center shadow">
                <p className="text-2xl font-bold text-green-600">{taskStats.completed}</p>
                <p className="text-xs text-gray-500">Completed</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center shadow">
                <p className="text-2xl font-bold text-red-600">{taskStats.overdue}</p>
                <p className="text-xs text-gray-500">Overdue</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 text-center shadow">
                <p className="text-2xl font-bold text-orange-600">{taskStats.high_priority}</p>
                <p className="text-xs text-gray-500">High Priority</p>
              </div>
            </div>
          )}

          {/* Export Buttons */}
          <div className="flex items-center justify-between bg-white rounded-xl shadow p-4">
            <p className="text-sm text-gray-600">
              {language === 'hi' 
                ? `${tasks.length} tasks दिख रहे हैं` 
                : `Showing ${tasks.length} tasks`}
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => handleExportExcel()}
                className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium shadow-sm"
                data-testid="export-excel-btn"
              >
                <span>📊</span>
                {language === 'hi' ? 'Excel Export' : 'Export Excel'}
              </button>
              <button
                onClick={() => handlePrintPDF()}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium shadow-sm"
                data-testid="print-pdf-btn"
              >
                <span>🖨️</span>
                {language === 'hi' ? 'Print / PDF' : 'Print / PDF'}
              </button>
            </div>
          </div>

          {/* Tasks Table */}
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="tasks-table">
                <thead className="bg-gray-50">
                  <tr>
                    {isPlatformAdmin && (
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                        {language === 'hi' ? '🏫 स्कूल' : '🏫 School'}
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                      {language === 'hi' ? 'छात्र' : 'Student'}
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">
                      {language === 'hi' ? 'कक्षा' : 'Class'}
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">
                      {language === 'hi' ? 'किसके लिए' : 'For'}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" style={{minWidth: '200px'}}>
                      {language === 'hi' ? 'Pending Fields' : 'Missing Fields'}
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">
                      {language === 'hi' ? 'Deadline' : 'Deadline'}
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">
                      {language === 'hi' ? 'स्थिति' : 'Status'}
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">
                      {language === 'hi' ? 'Action' : 'Action'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {tasks.length === 0 ? (
                    <tr>
                      <td colSpan={isPlatformAdmin ? 8 : 7} className="px-4 py-12 text-center text-gray-500">
                        <div className="text-4xl mb-2">📭</div>
                        {language === 'hi' ? 'कोई task नहीं मिला' : 'No tasks found'}
                      </td>
                    </tr>
                  ) : (
                    tasks.map((task) => (
                      <tr 
                        key={task.id} 
                        className={`hover:bg-gray-50 ${
                          task.status === 'completed' ? 'bg-green-50/50' :
                          isOverdue(task.deadline) ? 'bg-red-50/50' : ''
                        }`}
                      >
                        {isPlatformAdmin && (
                          <td className="px-4 py-3">
                            <p className="text-sm text-gray-700 font-medium truncate" style={{maxWidth: '150px'}} title={task.school_name}>
                              {task.school_name || '-'}
                            </p>
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-800">{task.student_name}</p>
                          <p className="text-xs text-gray-500">Section - {task.section_name || 'A'}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                            {task.class_name}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            task.assigned_to === 'teacher' 
                              ? 'bg-indigo-100 text-indigo-700' 
                              : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {task.assigned_to === 'teacher' 
                              ? (language === 'hi' ? 'शिक्षक' : 'Teacher')
                              : (language === 'hi' ? 'अभिभावक' : 'Parent')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1" style={{maxWidth: '280px'}}>
                            {task.missing_fields?.filter(f => 
                              !filters.priority || f.priority === filters.priority
                            ).map((f, idx) => (
                              <span 
                                key={idx}
                                className={`px-2 py-0.5 rounded text-xs ${
                                  f.priority === 'high' ? 'bg-red-100 text-red-700' :
                                  f.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}
                                title={language === 'hi' ? f.label_hi : f.label}
                              >
                                {getShortFieldName(f.field)}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs ${
                            isOverdue(task.deadline) ? 'text-red-600 font-medium' : 'text-gray-600'
                          }`}>
                            {formatDate(task.deadline)}
                            {isOverdue(task.deadline) && task.status === 'pending' && (
                              <span className="block text-red-500">⚠️ Overdue</span>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            task.status === 'completed' ? 'bg-green-100 text-green-700' :
                            task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {task.status === 'completed' ? '✅ Done' :
                             task.status === 'in_progress' ? '🔄 In Progress' :
                             '⏳ Pending'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {task.status !== 'completed' && (
                            <button
                              onClick={() => {
                                setSelectedTask(task);
                                loadStudentTasks(task.student_id);
                              }}
                              className="px-3 py-1.5 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600 transition"
                              data-testid={`fill-task-${task.id}`}
                            >
                              {language === 'hi' ? 'भरें' : 'Fill'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Load More / Pagination hint */}
          {tasks.length >= 50 && (
            <p className="text-center text-sm text-gray-500">
              {language === 'hi' 
                ? 'Filter का उपयोग करके और specific tasks देखें'
                : 'Use filters to see more specific tasks'}
            </p>
          )}
        </div>
      )}

      {/* Task Form Modal */}
      {selectedTask && (
        <TaskFormModal
          task={selectedTask}
          student={selectedStudent}
          onClose={() => {
            setSelectedTask(null);
            setSelectedStudent(null);
          }}
          onSubmit={handleCompleteTask}
          language={language}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
          toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          {toast.message}
          <button 
            onClick={() => setToast(null)}
            className="ml-4 text-white/80 hover:text-white"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
